/**
 * Potion registry + the vanilla brewing recipe graph. DOM-free.
 *
 * A potion stack is `{ id: POTION | SPLASH_POTION | LINGERING_POTION, dmg: potion index }`;
 * the index is the position in POTIONS below, which is persisted in saves, so only ever
 * APPEND to the table. Durations are ticks (vanilla: 3:00 = 3600, extended 8:00 = 9600).
 *
 *   potionOf(dmg)                 definition for a stack's `dmg`
 *   potionName(id, dmg)           "Splash Potion of Healing II"
 *   brewResult(ingredient, bottle) what one bottle becomes with that ingredient, or null
 *   isBrewIngredient(id)          may go in the stand's ingredient slot
 *   potionEffects(id, dmg)        the effects applied by drinking / splashing (scaled by form)
 */

import { NETHER_WART_IDS } from '../../blocks';
import { I } from '../itemIds';
import { FX, effectDef, effectLabel, formatDuration } from '../effects/effects';
import type { Stack } from '../types';

export interface PotionEffect { id: number; amp: number; dur: number }
export interface PotionDef {
  index: number;
  key: string;
  /** Display name without the container ("Healing II", "Awkward"). */
  label: string;
  effects: readonly PotionEffect[];
  /** Liquid colour (vanilla potion colour of the first effect, water blue otherwise). */
  color: number;
  /** No effect (Water Bottle, Mundane, Thick, Awkward). */
  base: boolean;
}

const WATER_BLUE = 0x385dc6;
const T3 = 3600, T8 = 9600;

/** [key, label, effect id, base duration, extended duration, strong duration, strong amp]. */
type Row = [string, string, number[], number, number, number, number];
// turtle master has two effects (Slowness + Resistance): handled by the explicit list below
const ROWS: Row[] = [
  ['night_vision', 'Night Vision', [FX.NIGHT_VISION], T3, T8, 0, 0],
  ['invisibility', 'Invisibility', [FX.INVISIBILITY], T3, T8, 0, 0],
  ['leaping', 'Leaping', [FX.JUMP_BOOST], T3, T8, 1800, 1],
  ['fire_resistance', 'Fire Resistance', [FX.FIRE_RESISTANCE], T3, T8, 0, 0],
  ['swiftness', 'Swiftness', [FX.SPEED], T3, T8, 1800, 1],
  ['slowness', 'Slowness', [FX.SLOWNESS], 1800, 4800, 400, 3],
  ['water_breathing', 'Water Breathing', [FX.WATER_BREATHING], T3, T8, 0, 0],
  ['healing', 'Healing', [FX.INSTANT_HEALTH], 1, 0, 1, 1],
  ['harming', 'Harming', [FX.INSTANT_DAMAGE], 1, 0, 1, 1],
  ['poison', 'Poison', [FX.POISON], 900, 1800, 432, 1],
  ['regeneration', 'Regeneration', [FX.REGENERATION], 900, 1800, 440, 1],
  ['strength', 'Strength', [FX.STRENGTH], T3, T8, 1800, 1],
  ['weakness', 'Weakness', [FX.WEAKNESS], 1800, 4800, 0, 0],
  ['slow_falling', 'Slow Falling', [FX.SLOW_FALLING], 1800, 4800, 0, 0],
];

export const POTIONS: PotionDef[] = [];
const BY_KEY = new Map<string, PotionDef>();

function push(key: string, label: string, effects: PotionEffect[], base = false): void {
  const def: PotionDef = {
    index: POTIONS.length, key, label, effects, base,
    color: effects.length ? colorOf(effects[0].id) : WATER_BLUE,
  };
  POTIONS.push(def);
  BY_KEY.set(key, def);
}

function colorOf(id: number): number { return effectDef(id)?.color ?? WATER_BLUE; }

// 0..3: bases (indices are persisted: append only)
push('water', 'Water Bottle', [], true);
push('mundane', 'Mundane Potion', [], true);
push('thick', 'Thick Potion', [], true);
push('awkward', 'Awkward Potion', [], true);
for (const [key, label, ids, dur, long, strongDur, strongAmp] of ROWS) {
  push(key, label, ids.map((id) => ({ id, amp: 0, dur })));
  if (long > 0) push('long_' + key, label + ' (Extended)', ids.map((id) => ({ id, amp: 0, dur: long })));
  if (strongDur > 0) push('strong_' + key, `${label} II`, ids.map((id) => ({ id, amp: strongAmp, dur: strongDur })));
}
// Turtle Master: Slowness IV + Resistance III for 20 s (extended 40 s; II: Slowness VI + Resistance IV for 20 s)
push('turtle_master', 'Turtle Master', [{ id: FX.SLOWNESS, amp: 3, dur: 400 }, { id: FX.RESISTANCE, amp: 2, dur: 400 }]);
push('long_turtle_master', 'Turtle Master (Extended)', [{ id: FX.SLOWNESS, amp: 3, dur: 800 }, { id: FX.RESISTANCE, amp: 2, dur: 800 }]);
push('strong_turtle_master', 'Turtle Master II', [{ id: FX.SLOWNESS, amp: 5, dur: 400 }, { id: FX.RESISTANCE, amp: 3, dur: 400 }]);

export const potionByKey = (key: string): PotionDef => BY_KEY.get(key) ?? POTIONS[0];
export const potionOf = (dmg: number): PotionDef => POTIONS[dmg] ?? POTIONS[0];

export type PotionForm = 'potion' | 'splash' | 'lingering';
export const isPotionItem = (id: number): boolean => id === I.POTION || id === I.SPLASH_POTION || id === I.LINGERING_POTION;
export const formOf = (id: number): PotionForm => (id === I.SPLASH_POTION ? 'splash' : id === I.LINGERING_POTION ? 'lingering' : 'potion');
export const itemOfForm = (f: PotionForm): number => (f === 'splash' ? I.SPLASH_POTION : f === 'lingering' ? I.LINGERING_POTION : I.POTION);

/** Stack of a potion in `form` (count 1). */
export function potionStack(key: string, form: PotionForm = 'potion'): Stack {
  return { id: itemOfForm(form), count: 1, dmg: potionByKey(key).index };
}

export function potionName(id: number, dmg: number): string {
  const p = potionOf(dmg);
  const f = formOf(id);
  if (p.base) {
    if (p.key === 'water') return f === 'potion' ? 'Water Bottle' : f === 'splash' ? 'Splash Water Bottle' : 'Lingering Water Bottle';
    return f === 'potion' ? p.label : (f === 'splash' ? 'Splash ' : 'Lingering ') + p.label;
  }
  const head = f === 'potion' ? 'Potion of ' : f === 'splash' ? 'Splash Potion of ' : 'Lingering Potion of ';
  return head + p.label;
}

/** Effects of a potion in its form: splash x0.75, lingering x0.25 duration (instants unchanged). */
export function potionEffects(id: number, dmg: number): PotionEffect[] {
  const k = formOf(id) === 'splash' ? 0.75 : formOf(id) === 'lingering' ? 0.25 : 1;
  return potionOf(dmg).effects.map((e) => ({
    id: e.id, amp: e.amp,
    dur: effectDef(e.id)?.instant ? e.dur : Math.max(1, Math.floor(e.dur * k)),
  }));
}

// --- recipe graph ---------------------------------------------------------------

const WART = NETHER_WART_IDS[0];
/** Ingredients that turn a Water Bottle into a Mundane Potion. */
const MUNDANE = [I.SUGAR, I.GHAST_TEAR, I.SPIDER_EYE, I.BLAZE_POWDER, I.MAGMA_CREAM, I.GLISTERING_MELON_SLICE, I.RABBIT_FOOT, I.GOLDEN_CARROT, I.REDSTONE];

/** [ingredient item id, from potion key, to potion key] */
const MIXES: Array<[number, string, string]> = [];
const mix = (ing: number, from: string, to: string): void => { MIXES.push([ing, from, to]); };

mix(WART, 'water', 'awkward');
for (const i of MUNDANE) mix(i, 'water', 'mundane');
mix(I.GLOWSTONE_DUST, 'water', 'thick');
mix(I.FERMENTED_SPIDER_EYE, 'water', 'weakness');
// Awkward Potion + effect ingredient
mix(I.GOLDEN_CARROT, 'awkward', 'night_vision');
mix(I.RABBIT_FOOT, 'awkward', 'leaping');
mix(I.MAGMA_CREAM, 'awkward', 'fire_resistance');
mix(I.SUGAR, 'awkward', 'swiftness');
mix(I.PUFFERFISH, 'awkward', 'water_breathing');
mix(I.GLISTERING_MELON_SLICE, 'awkward', 'healing');
mix(I.SPIDER_EYE, 'awkward', 'poison');
mix(I.GHAST_TEAR, 'awkward', 'regeneration');
mix(I.BLAZE_POWDER, 'awkward', 'strength');
mix(I.PHANTOM_MEMBRANE, 'awkward', 'slow_falling');
mix(I.TURTLE_SCUTE, 'awkward', 'turtle_master');
// Redstone: extended; Glowstone: level II
for (const k of ['night_vision', 'invisibility', 'leaping', 'fire_resistance', 'swiftness', 'slowness', 'water_breathing',
  'poison', 'regeneration', 'strength', 'weakness', 'slow_falling', 'turtle_master']) mix(I.REDSTONE, k, 'long_' + k);
for (const k of ['leaping', 'swiftness', 'slowness', 'healing', 'harming', 'poison', 'regeneration', 'strength', 'turtle_master']) {
  mix(I.GLOWSTONE_DUST, k, 'strong_' + k);
}
// Fermented Spider Eye: corruption
const F = I.FERMENTED_SPIDER_EYE;
mix(F, 'night_vision', 'invisibility'); mix(F, 'long_night_vision', 'long_invisibility');
for (const k of ['swiftness', 'leaping', 'fire_resistance', 'slow_falling']) {
  mix(F, k, 'slowness'); mix(F, 'long_' + k, 'long_slowness');
}
mix(F, 'strong_swiftness', 'slowness'); mix(F, 'strong_leaping', 'slowness');
mix(F, 'healing', 'harming'); mix(F, 'strong_healing', 'strong_harming');
mix(F, 'poison', 'harming'); mix(F, 'long_poison', 'harming'); mix(F, 'strong_poison', 'strong_harming');

export const BREW_MIXES: ReadonlyArray<readonly [number, string, string]> = MIXES;

const INGREDIENTS = new Set<number>([I.GUNPOWDER, I.DRAGON_BREATH, ...MIXES.map((m) => m[0])]);
export const isBrewIngredient = (id: number): boolean => INGREDIENTS.has(id);

const MIX_INDEX = new Map<string, number>();
for (const [ing, from, to] of MIXES) MIX_INDEX.set(`${ing}:${potionByKey(from).index}`, potionByKey(to).index);

/** What the bottle `bottle` becomes with `ingredient`, or null (the bottle is left unchanged). */
export function brewResult(ingredient: number, bottle: Stack): { id: number; dmg: number } | null {
  if (!isPotionItem(bottle.id) || bottle.count <= 0) return null;
  if (ingredient === I.GUNPOWDER) return bottle.id === I.POTION ? { id: I.SPLASH_POTION, dmg: bottle.dmg } : null;
  if (ingredient === I.DRAGON_BREATH) return bottle.id === I.SPLASH_POTION ? { id: I.LINGERING_POTION, dmg: bottle.dmg } : null;
  // potion-type mixes only apply to the drinkable form (vanilla: splash / lingering cannot be re-mixed)
  if (bottle.id !== I.POTION) return null;
  const to = MIX_INDEX.get(`${ingredient}:${bottle.dmg}`);
  return to === undefined ? null : { id: I.POTION, dmg: to };
}

/** Tooltip lines under a potion's name: "Swiftness II (1:30)" in the effect's colour class (vanilla blue / red). */
export function potionTooltipLines(id: number, dmg: number): string[] {
  const p = potionOf(dmg);
  if (p.effects.length === 0) return [p.key === 'water' ? '' : '§7No Effects'].filter(Boolean);
  return potionEffects(id, dmg).map((e) => {
    const def = effectDef(e.id);
    const dur = def?.instant ? '' : ` (${formatDuration(e.dur)})`;
    return (def?.beneficial ? '§9' : '§c') + effectLabel(e.id, e.amp) + dur;
  });
}
